import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = [
    "dateInput",
    "startTimeSelect",
    "startTimeContainer",
    "durationSelect",
    "quantitySelect",
    "availabilityMessage",
    "bookingFields",
    "tipPercentage",
    "customTipInput",
    "customTipAmount"
  ]

  static values = {
    availabilityUrl: String,
    orderUrl: String,
    rentalId: String,
    promoCodeUrl: String,
    giftCardUrl: String
  }

  connect() {
    this._slots = []
    this._updateOrderDebounced = this._debounce(() => this.updateOrder(), 300)
    // If there's already a date selected, load availability
    if (this.hasDateInputTarget && this.dateInputTarget.value) {
      this.loadAvailability(this.dateInputTarget.value)
    }
  }

  async onDateChange(event) {
    const date = event.target.value
    if (!date) return
    await this.loadAvailability(date)
  }

  async loadAvailability(date) {
    try {
      const url = `${this.availabilityUrlValue}?date=${date}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" }
      })
      const data = await response.json()

      if (data.available) {
        this._slots = data.start_times
        this.populateStartTimes(data.start_times)
        this.updateDurationPrices(data.duration_prices || {})
        this.startTimeContainerTarget.classList.remove("d-none")
        this.bookingFieldsTarget.classList.add("d-none") // hide until time chosen
        if (this.hasAvailabilityMessageTarget) {
          this.availabilityMessageTarget.classList.add("d-none")
        }
      } else {
        this._slots = []
        this.startTimeContainerTarget.classList.add("d-none")
        this.bookingFieldsTarget.classList.add("d-none")
        if (this.hasAvailabilityMessageTarget) {
          this.availabilityMessageTarget.classList.remove("d-none")
        }
      }
    } catch (error) {
      console.error("Error loading availability:", error)
    }
  }

  populateStartTimes(startTimes) {
    const select = this.startTimeSelectTarget
    select.innerHTML = '<option value="">Select start time...</option>'
    startTimes.forEach(({ label, value }) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      select.appendChild(option)
    })
  }

  onStartTimeChange() {
    const value = parseInt(this.startTimeSelectTarget.value)
    const slot = this._slots.find(s => s.value === value)

    if (slot) {
      this.filterDurations(slot.valid_duration_ids)
      this.populateQuantityOptions(slot.available_quantity)
      this.bookingFieldsTarget.classList.remove("d-none")
      this.updateOrder()
    } else {
      this.bookingFieldsTarget.classList.add("d-none")
    }
  }

  updateDurationPrices(prices) {
    this.element.querySelectorAll("[data-duration-id]").forEach(el => {
      const id = el.dataset.durationId
      const label = el.querySelector("[data-price-label]")
      if (!label) return

      const priceCents = id in prices ? prices[id] : parseInt(el.dataset.basePriceCents || "0", 10)
      const dollars = (priceCents / 100).toFixed(2)
      label.textContent = `$${dollars}`
    })
  }

  filterDurations(validIds) {
    const validStrings = validIds.map(String)
    this.element.querySelectorAll("[data-duration-id]").forEach(el => {
      const id = el.dataset.durationId
      if (validStrings.includes(id)) {
        el.classList.remove("d-none")
      } else {
        el.classList.add("d-none")
        const radio = el.querySelector("input[type='radio']")
        if (radio && radio.checked) radio.checked = false
      }
    })

    // Auto-select first visible duration if none is currently selected
    const anyChecked = this.element.querySelector("[data-duration-id]:not(.d-none) input[type='radio']:checked")
    if (!anyChecked) {
      const first = this.element.querySelector("[data-duration-id]:not(.d-none) input[type='radio']")
      if (first) first.checked = true
    }
  }

  populateQuantityOptions(max) {
    const select = this.quantitySelectTarget
    const current = parseInt(select.value) || 1
    select.innerHTML = ""
    for (let i = 1; i <= max; i++) {
      const option = document.createElement("option")
      option.value = i
      option.textContent = i
      if (i === Math.min(current, max)) option.selected = true
      select.appendChild(option)
    }
  }

  updateOrder() {
    const formData = this.collectOrderData()
    this.postToUrl(this.orderUrlValue, formData)
  }

  updateOrderDebounced() {
    this._updateOrderDebounced()
  }

  toggleCustomTipInput() {
    if (!this.hasCustomTipInputTarget) return
    const customRadio = this.tipPercentageTargets.find(r => r.value === "custom")
    const isCustom = customRadio && customRadio.checked
    this.customTipInputTarget.classList.toggle("d-none", !isCustom)
    if (isCustom && this.hasCustomTipAmountTarget) {
      this.customTipAmountTarget.focus()
    }
  }

  applyPromoCode(event) {
    const input = this.element.querySelector('[data-rental-promo-target]') ||
                  this.element.querySelector('#rental_promo_code')
    if (!input || !input.value) return

    const formData = this.collectOrderData()
    formData.append("promo_code", input.value)
    this.postToUrl(this.promoCodeUrlValue, formData)
  }

  removePromoCode() {
    const formData = this.collectOrderData()
    this.deleteToUrl(this.promoCodeUrlValue, formData)
  }

  applyGiftCard() {
    const input = this.element.querySelector('#rental_gift_card_code')
    if (!input || !input.value) return

    const formData = this.collectOrderData()
    formData.append("gift_card_code", input.value)
    this.postToUrl(this.giftCardUrlValue, formData)
  }

  removeGiftCard() {
    const formData = this.collectOrderData()
    this.deleteToUrl(this.giftCardUrlValue, formData)
  }

  collectOrderData() {
    const formData = new FormData()
    formData.append("rental_id", this.rentalIdValue)

    const checkedDuration = this.element.querySelector("input[name='booking[rental_duration_id]']:checked")
    if (checkedDuration) {
      formData.append("rental_duration_id", checkedDuration.value)
    }
    if (this.hasQuantitySelectTarget) {
      formData.append("quantity", this.quantitySelectTarget.value)
    }
    if (this.hasDateInputTarget) {
      formData.append("reservation_on", this.dateInputTarget.value)
    }
    if (this.hasStartTimeSelectTarget) {
      formData.append("start_time", this.startTimeSelectTarget.value)
    }
    if (this.hasTipPercentageTarget) {
      const checked = this.tipPercentageTargets.find(r => r.checked)
      if (checked) {
        formData.append("tip_percentage", checked.value)
        if (checked.value === "custom" && this.hasCustomTipAmountTarget) {
          formData.append("tip_amount", this.customTipAmountTarget.value)
        } else {
          this.customTipAmountTarget.value = 0
        }
      }
    }
    return formData
  }

  _debounce(fn, delay) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => fn.apply(this, args), delay)
    }
  }

  postToUrl(url, formData) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, Accept: "text/vnd.turbo-stream.html" },
      body: formData
    })
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html))
      .catch((error) => console.error("Error:", error))
  }

  deleteToUrl(url, formData) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken, Accept: "text/vnd.turbo-stream.html" },
      body: formData
    })
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html))
      .catch((error) => console.error("Error:", error))
  }
}
